import { AnalyticsScript, Loading, ModalOrder } from '@p2p/components';
import { NotFoundErrorPage } from '@p2p/pages/Errors';
import { useGetMerchantOrderQuery } from '@p2p/services';
import {
  isMainPageDesign,
  isTerminalOrderStatus,
  MSP_PAGE,
  ORDER_STATUS,
  PROOF_PAGE,
  successStatuses,
} from '@p2p/shared/constants';
import { isUUID, takeCardPaymentRedirect } from '@p2p/shared/functions';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { PaymentPage } from '../Payment';
import { ThemedTransferPage } from '../ThemedTransferPage';
import { TransferPage } from '../TransferPage';

const finishedThemeListNames = [
  'fresh',
  'lex',
  'jet',
  'drip',
  'volna',
  'starda',
  'drip',
  'legzo',
  'izzi',
  'sol',
  'onego',
  'monro',
];

const ORDER_POLLING_INTERVAL = 1000;

const LandingRenderContent = () => {
  const [isShow, setIsShow] = useState(false);
  const [isPlayed, setIsPlayed] = useState(false);
  const { id } = useParams();

  const isValidId = id ? isUUID(id) : false;

  const [pollingInterval, setPollingInterval] = useState<number>(ORDER_POLLING_INTERVAL);

  const { isLoading, data: order } = useGetMerchantOrderQuery(id ?? '', {
    pollingInterval,
    skip: !id || !isValidId,
  });

  const theme = order?.payment_form?.split('_')?.[0];

  // P2P-2864 (запрос PayGo 06.10.2026): у заявки со страницей оплаты провайдера
  // (provider_page_url, SimplePay sber_pay) плательщик сразу уходит туда — и с
  // любого шаблона страницы, и мимо выбора банка/экрана с QR. Нужно мерчантам с
  // create_order?type=api: они сами ведут плательщика на нашу /transfer/<id>
  // (с ?type=redirect бэкенд и так отдаёт ссылку провайдера в Location). Только
  // новая заявка и не после возврата от провайдера по return_url
  // (?from_provider=1) — тогда плательщик видит нашу страницу и ждёт статуса.
  // Решение — один раз, по первому ответу check_order (дальше идёт поллинг).
  const providerPageDecided = useRef(false);
  const [providerPageRedirect, setProviderPageRedirect] = useState<string | null>(null);

  useEffect(() => {
    if (!order || providerPageDecided.current) {
      return;
    }
    providerPageDecided.current = true;
    if (!order.provider_page_url || order.status !== ORDER_STATUS.BRAND_NEW) {
      return;
    }
    const link = takeCardPaymentRedirect(order.id, order.provider_page_url);
    if (link) {
      setProviderPageRedirect(link);
      // replace, а не assign: «Назад» со страницы провайдера не возвращает к нам по кругу.
      window.location.replace(link);
    }
  }, [order]);

  const openModal = () => setIsShow(true);
  const closeModal = () => setIsShow(false);
  const onPlayed = () => setIsPlayed(true);

  useEffect(() => {
    if (successStatuses.includes(String(order?.status))) {
      setIsShow(false);
    }
    // P2P-2776: заявка в конечном статусе больше не изменится -- гасим поллинг, иначе
    // оставленная открытой вкладка опрашивает check_order раз в секунду часами.
    if (isTerminalOrderStatus(order?.status)) {
      setPollingInterval(0);
    }
  }, [order?.status]);

  if (!isValidId) {
    return <NotFoundErrorPage />;
  }

  if (isLoading || providerPageRedirect) {
    return <Loading />;
  }

  // P2P-3036, P2P-3037: gazprom_page и ozon_page — та же страница main_page, другой только
  // блок быстрой оплаты.
  if (isMainPageDesign(order?.payment_form)) {
    return (
      <>
        <PaymentPage />
        <AnalyticsScript />
      </>
    );
  }

  if (order?.payment_form === 'msp_page') {
    return <Navigate to={`${MSP_PAGE}/${id}`} replace />;
  }

  // P2P-2880: страница с обязательной загрузкой PDF-чека.
  if (order?.payment_form === 'proof_page') {
    return <Navigate to={`${PROOF_PAGE}/${id}`} replace />;
  }

  return (
    <>
      {isShow && <ModalOrder onClose={closeModal} onPlayed={onPlayed} uid={id} key={+isShow} />}
      {finishedThemeListNames.includes(theme!) ? (
        <ThemedTransferPage order={order} openModal={openModal} isPlayed={isPlayed} />
      ) : (
        <TransferPage openModal={openModal} isPlayed={isPlayed} />
      )}
      <AnalyticsScript />
    </>
  );
};

export default LandingRenderContent;
